// characterRelationships.ts
export const NON_STORY_SENTINEL = 'Non Story Chapter';
const CHAR_PLACEHOLDER_PREFIX = 'char_';

export type RelationshipGender = 'male' | 'female' | 'unknown';
export type RelationshipFormality = 'Formal' | 'Informal';

export interface CharacterRelationshipRow {
  id: string;
  characterAName: string;
  characterAGender: RelationshipGender;
  characterBName: string;
  characterBGender: RelationshipGender;
  formality: RelationshipFormality;
  formalityAtoB?: RelationshipFormality;
  formalityBtoA?: RelationshipFormality;
  characterAId?: string;
  characterBId?: string;
}

const createRowId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

const normalizePlaceholderCandidate = (value?: string | null): string =>
  (value || '')
    .trim()
    .replace(/^[\s"'`“”‘’([{<]+/, '')
    .replace(/[\s"'`“”‘’)\]}>.,:;!?]+$/, '')
    .trim()
    .toLowerCase();

export const isCharacterPlaceholderValue = (value?: string | null): boolean =>
  normalizePlaceholderCandidate(value).startsWith(CHAR_PLACEHOLDER_PREFIX);

export const RELATIONSHIP_GENDERS: RelationshipGender[] = ['male', 'female', 'unknown'];
export const RELATIONSHIP_FORMALITIES: RelationshipFormality[] = ['Formal', 'Informal'];

export const createEmptyRelationshipRow = (): CharacterRelationshipRow => ({
  id: createRowId(),
  characterAName: '',
  characterAGender: 'unknown',
  characterBName: '',
  characterBGender: 'unknown',
  formality: 'Informal',
  formalityAtoB: 'Informal',
  formalityBtoA: 'Informal',
});

export const isNonStoryRelationship = (text?: string | null): boolean => {
  if (!text) return false;
  return text.trim().toLowerCase() === NON_STORY_SENTINEL.toLowerCase();
};

const LEGACY_PAIR_REGEX =
  /^([^()|~\n]+?)\s*\((male|female|unknown)\)\s*&\s*([^()|~\n]+?)\s*\((male|female|unknown)\)$/i;
const LEGACY_PAIR_SHAPE = /\([^\n)]*\)\s*&/;
const LEGACY_FORMALITY_REGEX = /^(?:Formality\s*:\s*)?(Formal|Informal)(?:\s+pronouns)?\.?$/i;

export class LegacyRelationshipFormatError extends Error {
  constructor() {
    super('Saved legacy relationships need review or a new character analysis before editing.');
    this.name = 'LegacyRelationshipFormatError';
  }
}

const parseLegacyRelationshipBlocks = (lines: string[]): CharacterRelationshipRow[] => {
  const nonblank = lines.filter(Boolean);
  if (!nonblank.some((line) => LEGACY_PAIR_SHAPE.test(line))) return [];
  const rows: CharacterRelationshipRow[] = [];
  const pairs = new Set<string>();
  for (let i = 0; i < nonblank.length; i += 2) {
    const match = nonblank[i].match(LEGACY_PAIR_REGEX);
    const label = nonblank[i + 1]?.match(LEGACY_FORMALITY_REGEX);
    if (!match || !label) throw new LegacyRelationshipFormatError();
    const [, rawNameA, genderA, rawNameB, genderB] = match;
    const characterAName = rawNameA.trim();
    const characterBName = rawNameB.trim();
    const names = [characterAName.toLowerCase(), characterBName.toLowerCase()].sort();
    const key = JSON.stringify(names);
    if (!characterAName || !characterBName || names[0] === names[1] || pairs.has(key) ||
        isCharacterPlaceholderValue(characterAName) || isCharacterPlaceholderValue(characterBName)) {
      throw new LegacyRelationshipFormatError();
    }
    pairs.add(key);
    const formality = (label[1].charAt(0).toUpperCase() + label[1].slice(1).toLowerCase()) as RelationshipFormality;
    rows.push({
      id: createRowId(), characterAName, characterBName,
      characterAGender: genderA.toLowerCase() as RelationshipGender,
      characterBGender: genderB.toLowerCase() as RelationshipGender,
      formality, formalityAtoB: formality, formalityBtoA: formality,
    });
  }
  return rows;
};

export const parseRelationshipText = (
  text?: string | null,
  options?: { characterLookup?: Record<string, string> }
): CharacterRelationshipRow[] => {
  if (!text || isNonStoryRelationship(text)) {
    return [];
  }

  const trimmed = text.trim();
  if (!trimmed) {
    return [];
  }

  const lines = trimmed.split(/\r?\n/).map((line) => line.trim());
  const rows: CharacterRelationshipRow[] = [];
  const lookup = options?.characterLookup;

  const tildeLines = lines.filter((line) => line.startsWith('~') && line.endsWith('~'));
  if (tildeLines.length > 0) {
    if (lines.some((line) => !tildeLines.includes(line) && LEGACY_PAIR_SHAPE.test(line))) {
      throw new LegacyRelationshipFormatError();
    }
    tildeLines.forEach((line) => {
      const payload = line.slice(1, -1).split('|');
      if (payload[0]?.toLowerCase() === 'v2' && payload.length === 7) {
        const [, characterAId, genderA, characterBId, genderB, formalityAtoB, formalityBtoA] =
          payload;
        const normalizedAId = characterAId?.trim() || '';
        const normalizedBId = characterBId?.trim() || '';
        const resolvedAName = (lookup?.[normalizedAId] || '').trim();
        const resolvedBName = (lookup?.[normalizedBId] || '').trim();
        const characterAName = resolvedAName || (isCharacterPlaceholderValue(normalizedAId) ? '' : normalizedAId);
        const characterBName = resolvedBName || (isCharacterPlaceholderValue(normalizedBId) ? '' : normalizedBId);
        if (
          !characterAName ||
          !characterBName ||
          isCharacterPlaceholderValue(characterAName) ||
          isCharacterPlaceholderValue(characterBName)
        ) {
          return;
        }
        const normalizedAtoB = (formalityAtoB || 'Informal').trim();
        const normalizedBtoA = (formalityBtoA || normalizedAtoB).trim();
        const normalizedFormality = normalizedAtoB;
        rows.push({
          id: createRowId(),
          characterAId: normalizedAId,
          characterAName,
          characterAGender: (genderA?.toLowerCase() as RelationshipGender) || 'unknown',
          characterBId: normalizedBId,
          characterBName,
          characterBGender: (genderB?.toLowerCase() as RelationshipGender) || 'unknown',
          formality: (normalizedFormality.charAt(0).toUpperCase() +
            normalizedFormality.slice(1).toLowerCase()) as RelationshipFormality,
          formalityAtoB: (normalizedAtoB.charAt(0).toUpperCase() +
            normalizedAtoB.slice(1).toLowerCase()) as RelationshipFormality,
          formalityBtoA: (normalizedBtoA.charAt(0).toUpperCase() +
            normalizedBtoA.slice(1).toLowerCase()) as RelationshipFormality,
        });
        return;
      }
      if (payload.length !== 5) {
        return;
      }
      const [characterAName, genderA, characterBName, genderB, formality] = payload;
      const normalizedAName = characterAName.trim();
      const normalizedBName = characterBName.trim();
      if (
        !normalizedAName ||
        !normalizedBName ||
        isCharacterPlaceholderValue(normalizedAName) ||
        isCharacterPlaceholderValue(normalizedBName)
      ) {
        return;
      }
      const normalizedFormality = (formality || 'Informal').trim();
      rows.push({
        id: createRowId(),
        characterAName: normalizedAName,
        characterAGender: (genderA?.toLowerCase() as RelationshipGender) || 'unknown',
        characterBName: normalizedBName,
        characterBGender: (genderB?.toLowerCase() as RelationshipGender) || 'unknown',
        formality: (normalizedFormality.charAt(0).toUpperCase() +
          normalizedFormality.slice(1).toLowerCase()) as RelationshipFormality,
        formalityAtoB: (normalizedFormality.charAt(0).toUpperCase() +
          normalizedFormality.slice(1).toLowerCase()) as RelationshipFormality,
        formalityBtoA: (normalizedFormality.charAt(0).toUpperCase() +
          normalizedFormality.slice(1).toLowerCase()) as RelationshipFormality,
      });
    });
    return rows;
  }

  return parseLegacyRelationshipBlocks(lines);
};

export const rowsToRequestPayload = (rows: CharacterRelationshipRow[]) =>
  rows.map((row) => ({
    characterAId: row.characterAId,
    characterAName: row.characterAName.trim(),
    characterAGender: row.characterAGender,
    characterBId: row.characterBId,
    characterBName: row.characterBName.trim(),
    characterBGender: row.characterBGender,
    formality: row.formality,
    formalityAtoB: row.formalityAtoB ?? row.formality,
    formalityBtoA: row.formalityBtoA ?? row.formality,
  }));

export const estimateRelationshipCreditCost = (wordCount: number): number => {
  if (!wordCount || wordCount <= 0) return 0;
  return Math.ceil(wordCount * 0.1);
};
